import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'

export type SpaceSummary = {
  space: ProjectDocument
  projectCount: number
  openCount: number
  overdueCount: number
  /** Closed ÷ all top-level tasks, 0–1. 0 when the space has no tasks. */
  progress: number
  /** First few open tasks, for the card preview. */
  previewTasks: Array<TaskDocument>
}

type BuildOptions = {
  spaces: ReadonlyArray<ProjectDocument>
  projects: ReadonlyArray<ProjectDocument>
  tasks: ReadonlyArray<TaskDocument>
  openStatusIds: ReadonlyArray<string>
  /** Start of today in ms; tasks due before this are overdue. */
  startOfToday: number
  previewSize?: number
}

/**
 * Roll tasks up to their space for the Home "Spaces" cards (design 1d).
 * A task belongs to the space of its project: the project itself when it is
 * a root project (a space), otherwise its parent. Only top-level, unarchived
 * tasks count.
 */
export function buildSpaceSummaries({
  spaces,
  projects,
  tasks,
  openStatusIds,
  startOfToday,
  previewSize = 2,
}: BuildOptions): Array<SpaceSummary> {
  const spaceIdByProjectId = new Map<string, string>()
  const projectCountBySpace = new Map<string, number>()
  for (const project of projects) {
    const spaceId = project.parentProjectId ?? project.id
    spaceIdByProjectId.set(project.id, spaceId)
    if (project.parentProjectId && !project.isDeleted) {
      projectCountBySpace.set(spaceId, (projectCountBySpace.get(spaceId) ?? 0) + 1)
    }
  }

  const openIds = new Set(openStatusIds)
  const buckets = new Map<string, { total: number; open: Array<TaskDocument>; overdue: number }>()
  for (const task of tasks) {
    if (task.parentId || task.isArchived || !task.projectId) continue
    const spaceId = spaceIdByProjectId.get(task.projectId)
    if (!spaceId) continue
    const bucket = buckets.get(spaceId) ?? { total: 0, open: [], overdue: 0 }
    bucket.total += 1
    if (openIds.has(task.statusId)) {
      bucket.open.push(task)
      if (task.dueDate != null && task.dueDate < startOfToday) bucket.overdue += 1
    }
    buckets.set(spaceId, bucket)
  }

  return spaces.map((space) => {
    const bucket = buckets.get(space.id)
    const total = bucket?.total ?? 0
    const open = bucket?.open ?? []
    return {
      space,
      projectCount: projectCountBySpace.get(space.id) ?? 0,
      openCount: open.length,
      overdueCount: bucket?.overdue ?? 0,
      progress: total === 0 ? 0 : (total - open.length) / total,
      previewTasks: open.slice(0, previewSize),
    }
  })
}
